import { useEffect, useState } from "react";
import { CustomCheckbox } from "components/CustomCheckbox/CustomCheckbox";
import DialogAlertAction from "components/DialogAlertAction/DialogAlertAction";
import CustomButton from "components/CustomButton/CustomButton";
import Preloader from "components/Preloader/Preloader";
import CalendarIcon from "icons/CalendarIcon";
import TimerIcon from "icons/TimerIcon";
import { getText } from "components/CustomTimePicker/utils";
import {
    getDateMonthString,
    getDatePartsByMillis,
    getDayOfDateByTimestamp,
    getTodayWithoutHourInMillis
} from "components/CustomDatePicker/utils/helpers";
import useMainContext from "utils/context/useMainContext";
import { Content, Metadata, MetadataText, ModalTitle, Toggle } from "./styles";

const getTimeParts = minutes => [Math.floor(minutes / 60), minutes % 60];
const getTimeText = (timeStart, timeEnd) =>
    `${getText(getTimeParts(timeStart))} - ${getText(getTimeParts(timeEnd))}`;
const getDateText = date => {
    const [year, month, dateNumber] = getDatePartsByMillis(date);
    return `${getDayOfDateByTimestamp(date).substring(0, 3)}, ${getDateMonthString([month, dateNumber])} ${year}`;
};

const ManagementAvailability = ({
    dateTimeData,
    inactivity,
    showUpdateDialog,
    adviser,
    reloadData,
    available,
    changesCannotBeDone,
    slotLabel,
    slotDemonstrative,
    switchAvailability
}) => {
    const mainContext = useMainContext();
    const [activatedHour, setActivatedHour] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [loading, setLoading] = useState(false);
    const { date, timeStart, timeEnd } = dateTimeData;

    useEffect(() => {
        if (!showConfirmation) setActivatedHour(null);
    }, [showConfirmation]);

    const toggleConfirmation = (value = null) => {
        setShowConfirmation(value === null ? !showConfirmation : value);
    };
    const changeActivatedHour = () => {
        setActivatedHour(!available);
        setTimeout(toggleConfirmation, 330);
    };
    const getSuccessMessage = () => {
        const monthDate = getDatePartsByMillis(date).slice(1);
        const todayPrefix = date === -1 || getTodayWithoutHourInMillis() === date ? "Hoy " : "";
        return {
            message: `${todayPrefix}${getDateMonthString(monthDate, 1)} se ha ${activatedHour ? "activado" : "inactivado"} a ${adviser.name} en el horario de ${getTimeText(timeStart, timeEnd)}`,
            variant: "success",
            milis: 5000
        };
    };
    const switchShift = async () => {
        setLoading(true);
        toggleConfirmation();
        const success = await switchAvailability({
            activatedHour,
            inactivity,
            dateTimeData
        });
        setLoading(false);
        if (!success) {
            mainContext.setShowDialogUnexpectedError(true);
            return;
        }
        await reloadData(getSuccessMessage());
    };
    const confirmationProps = {
        show: showConfirmation,
        toggleShow: toggleConfirmation
    };
    const confirmation = !available
        ? {
            title: `¿Estás seguro(a) de activar ${slotDemonstrative} ${slotLabel}?`,
            content: "Recuerda que al activar este horario se verá visible para el estudiante y podrá reservar una asesoría"
        }
        : {
            title: `¿Estás seguro(a) de inactivar ${slotDemonstrative} ${slotLabel}?`,
            content: "Recuerda que al inactivar este horario ya no será visible para el estudiante en la app"
        };

    return (
        <>
            <Content>
                <ModalTitle>{available ? "Turno disponible" : "Otras actividades"}</ModalTitle>
                <Metadata>
                    <TimerIcon size={24}/>
                    <MetadataText>{getTimeText(timeStart, timeEnd)}</MetadataText>
                </Metadata>
                <Metadata>
                    <CalendarIcon size={24}/>
                    <MetadataText>{getDateText(date)}</MetadataText>
                </Metadata>
                <Toggle>
                    <CustomCheckbox
                        checked={activatedHour === null ? !available : !activatedHour}
                        disabled={changesCannotBeDone()}
                        onChange={changeActivatedHour}
                        variant="switch"
                        padding="0"
                        labelColor="var(--light-gray-4)"
                        label={`Inactivar ${slotLabel}`}/>
                </Toggle>
            </Content>
            <Preloader message="Cambiando turno..." hidden={!loading}/>
            {showUpdateDialog &&
                <DialogAlertAction {...confirmationProps} {...confirmation}>
                    <CustomButton
                        onClick={() => toggleConfirmation()}
                        variant="quintinary"
                        width="130px"
                        text="No, cancelar"/>
                    <CustomButton
                        onClick={switchShift}
                        width="130px"
                        variant="quaternary"
                        text={!available ? "Sí, activar" : "Si, inactivar"}/>
                </DialogAlertAction>}
        </>
    );
};

export default ManagementAvailability;
